import { Control, Controller, Path } from 'react-hook-form';
import { Autocomplete, Checkbox, TextField } from '@mui/material';
import useGetRosterQualificationsPreferred from 'components/roster-portal/plan-units/Preferences/hooks/useGetRosterQualificationsPreferred';
import { useGetQualifications } from 'queries';
import { Icon } from '@sdb/planning-ui';
import { StyledBoxFlexCenter } from '../Box/Box.styles';
import { StyledQualificationIconContainer } from './ControlledQualificationSelector.styles';

type ChangeDiff = {
  addedIds: string[];
  removedIds: string[];
  nextIds: string[];
  prevIds: string[];
  addedOption?: any;
  removedOption?: any;
};

type ControlledQualificationSelectorProps<
  FormValues extends Record<string, any>
> = {
  control: Control<any>;
  name: Path<FormValues>;
  label: string;
  costCenterId: number;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  onChangeDiff?: (diff: ChangeDiff) => void;
  onAddOption?: (option: any) => void;
  onRemoveOption?: (option: any) => void;
  disableClearable?: boolean;
};

export default function ControlledQualificationSelector<
  FormValues extends Record<string, any>
>({
  control,
  name,
  label,
  costCenterId,
  placeholder,
  disabled,
  fullWidth,
  required,
  onChangeDiff,
  onAddOption,
  onRemoveOption,
  disableClearable = true
}: ControlledQualificationSelectorProps<FormValues>) {
  const {
    data: preferredQualifications = [],
    isLoading: isLoadingPreferredQualifications
  } = useGetRosterQualificationsPreferred({ costCenterId });

  const { data: qualifications = [], isLoading: isLoadingQualifications } =
    useGetQualifications();

  // mark preferred and merge w/o duplicates
  const updatedPreferredQualifications = preferredQualifications.map(
    (qualification) => ({
      ...qualification,
      isPreferred: true
    })
  );
  const uniqueQualifications = Array.from(
    new Map(
      [...qualifications, ...updatedPreferredQualifications].map((item) => [
        item.id,
        item
      ])
    ).values()
  );

  const loading = isLoadingPreferredQualifications || isLoadingQualifications;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const prevIds: string[] = Array.isArray(field.value) ? field.value : [];
        const value = uniqueQualifications.filter((q) =>
          prevIds.includes(q.id)
        );

        return (
          <Autocomplete
            multiple
            options={uniqueQualifications}
            loading={loading}
            limitTags={2}
            value={value}
            onChange={(e, newOptions, reason, details) => {
              const nextIds = newOptions.map((o) => o.id);

              // compute diffs once
              const addedIds = nextIds.filter((id) => !prevIds.includes(id));
              const removedIds = prevIds.filter((id) => !nextIds.includes(id));

              // optional fine-grained hooks
              if (reason === 'selectOption' && details?.option) {
                onAddOption?.(details.option);
              } else if (reason === 'removeOption' && details?.option) {
                onRemoveOption?.(details.option);
              } else if (reason === 'clear') {
                // all removed
              }

              onChangeDiff?.({
                addedIds,
                removedIds,
                nextIds,
                prevIds,
                addedOption:
                  reason === 'selectOption' ? details?.option : undefined,
                removedOption:
                  reason === 'removeOption' ? details?.option : undefined
              });

              // keep form value as IDs
              field.onChange(nextIds);
            }}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disabled={disabled}
            disableClearable={disableClearable}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                    size="small"
                  />
                  <StyledBoxFlexCenter>
                    {option?.isPreferred && (
                      <StyledQualificationIconContainer iconSize="MEDIUM">
                        <Icon
                          type="star-fill"
                          color="primary"
                          fontSize="small"
                        />
                      </StyledQualificationIconContainer>
                    )}
                    {option?.name || ''}
                  </StyledBoxFlexCenter>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                variant="outlined"
                size="small"
                fullWidth={fullWidth}
                required={required}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        );
      }}
    />
  );
}
